import React, { useState } from 'react'
import { Box, Card, CardContent, Grid, styled, Tooltip } from '@mui/material'
import { NewTable } from '../../../components/datatable/NewTable'
import { AlignedHeadlessTableBox, AlignedTableBox } from '../AlignedTableWrappers'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { isNil, keyBy } from 'lodash'
import SubscriberDialog from './indicatorDialogs/subscriber/SubscriberDialog'
import { CATEGORY_CODES, countGroupIndicators, countIndicators, formatIndicatorCount, formatIndicatorValue, getCountPercent, getFinancialIndicators, getNetworkIndicators, getQualityIndicators, getStatusFromCode, getSubscriberIndicators } from 'rpqs/utils/RPQSUtils'
import NetworkDialog from './indicatorDialogs/network/NetworkDialog'
import QualityDialog from './indicatorDialogs/quality/QualityDialog'
import FinancialDialog from './indicatorDialogs/financial/FinancialDialog'
import { FINANCIAL, FINANCIAL_COLOR, GREY_BG, NETWORK, NETWORK_COLOR, P103_2B, QUALITY, QUALITY_COLOR, SUBSCRIBER, SUBSCRIBER_COLOR } from 'rpqs/constants/RpqsConstant'
import { CardTitle } from 'components/card/NewCard'
import Search from '@mui/icons-material/Search'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import SelectChipIndicator from './SelectChipIndicator'
import { normalizeString } from 'utils/StringUtil'
import IndicatorCategoryCard from '../IndicatorCategoryCard'
import { rpqsIndicatorShape } from 'rpqs/constants/RpqsShapes'
import RpqsIndicatorDialog from './indicatorDialogs/RpqsIndicatorDialog'
import RpqsIndicatorDialogP103_2B from './indicatorDialogs/network/RpqsIndicatorDialogP103_2B'

const DISABLED_COLOR = 'rgba(0, 0, 0, 0.38)'

const getHeaders = year => [
    { key: 'indicatorDenomination', value: i18n.indicatorDenomination, width: '30%', noSort: true },
    { key: 'indicator', value: i18n.indicator, width: '8%', noSort: true },
    { key: 'previousValue', value: `${year - 1}`, width: '8%', noSort: true },
    { key: 'value', value: `${year}`, width: '10%', noSort: true },
    { key: 'unit', value: i18n.unit, width: '13%', noSort: true },
    { key: 'count', value: i18n.rpqsFilledIndicators, width: '11%', noSort: true },
    { key: 'status', value: i18n.status, width: '20%', noSort: true },
]

const CATEGORY_CARD_WIDTH = 200

const IndicatorRowsTableBox = styled(AlignedHeadlessTableBox)({
    '& tbody tr': {
        height: 40,
    },
})

const CurrentValueCell = ({ value = '', notes = '' }) => (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <span>{value}</span>
        {notes ? (
            <Tooltip title={notes}>
                <StickyNote2Icon fontSize='small' color='action'/>
            </Tooltip>
        ) : <div/>}
    </Box>
)

CurrentValueCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    notes: PropTypes.string,
}

export const CategoryPanel = ({
    backIndicators = [],
    category = '',
    searchValue = '',
    rpqs = {},
    egId,
    refValues = {},
    onSaved = () => {},
}) => {
    const [dialogOpen, setDialogOpen] = useState(false)
    const [codeIndicatorPanel, setCodeIndicatorPanel] = useState('')
    const [dialogTitle, setDialogTitle] = useState('')
    const [branchedDialogOpen, setBranchedDialogOpen] = useState(false)
    const [branchedMainCode, setBranchedMainCode] = useState('')

    const backMainByCode = keyBy(backIndicators.filter(ind => !ind.parent), 'code')

    const handleClickRow = (row) => {
        if (row.isBranched) {
            setBranchedMainCode(row.code)
            setBranchedDialogOpen(true)
        } else {
            setCodeIndicatorPanel(row.code)
            setDialogTitle(`${row.indicator} - ${row.indicatorDenomination}`)
            setDialogOpen(true)
        }
    }

    const getCategoryElements = (categ) => {
        switch (categ) {
            case SUBSCRIBER:
                return {
                    indicators: getSubscriberIndicators(),
                    color: SUBSCRIBER_COLOR,
                    dialog: <SubscriberDialog open={dialogOpen} setOpen={setDialogOpen} codeIndicator={codeIndicatorPanel} dialogTitle={dialogTitle}/>,
                }
            case NETWORK:
                return {
                    indicators: getNetworkIndicators(),
                    color: NETWORK_COLOR,
                    dialog: <NetworkDialog open={dialogOpen} setOpen={setDialogOpen} codeIndicator={codeIndicatorPanel} dialogTitle={dialogTitle}/>,
                }
            case QUALITY:
                return {
                    indicators: getQualityIndicators(),
                    color: QUALITY_COLOR,
                    dialog: <QualityDialog open={dialogOpen} setOpen={setDialogOpen} codeIndicator={codeIndicatorPanel} dialogTitle={dialogTitle}/>,
                }
            case FINANCIAL:
                return {
                    indicators: getFinancialIndicators(),
                    color: FINANCIAL_COLOR,
                    dialog: <FinancialDialog open={dialogOpen} setOpen={setDialogOpen} codeIndicator={codeIndicatorPanel} dialogTitle={dialogTitle}/>,
                }
            default:
                return {}
        }
    }

    const { color, indicators, dialog } = getCategoryElements(category)

    const filteredIndicators = searchValue ?
        indicators.filter(indicatorObj => (
            normalizeString(indicatorObj.indicator).startsWith(searchValue) || normalizeString(i18n[indicatorObj.indicatorDenomination]).startsWith(searchValue)
        )) :
        indicators

    const indicatorRows = filteredIndicators.map((indicator) => {
        const backInd = backMainByCode[indicator.code]
        return {
            ...indicator,
            indicatorDenomination: backInd?.name || i18n[indicator.indicatorDenomination] || i18n.unknown,
            unit: backInd?.unit ?? indicator.unit,
            isBranched: !!backInd,
            value: formatIndicatorValue(backInd?.value, backInd?.fieldType),
            previousValue: formatIndicatorValue(backInd?.previousValue, backInd?.fieldType),
            status: backInd && getStatusFromCode(backInd.status),
            notes: backInd?.notes,
            count: !!backInd && formatIndicatorCount(countGroupIndicators(backIndicators, indicator.code)),
        }
    })

    const categoryCount = countIndicators(backIndicators, ind => ind.category === CATEGORY_CODES[category])

    const cell = (row, value) => ({ value, fontColor: row.isBranched ? 'black' : DISABLED_COLOR })

    const tableRows = indicatorRows.map(row => ({
        indicatorRow: row,
        indicatorDenomination: cell(row, row.indicatorDenomination),
        indicator: cell(row, row.indicator),
        previousValue: cell(row, row.previousValue !== '' && !isNil(row.previousValue) && (
            <Box sx={{ backgroundColor: GREY_BG, display: 'flex', alignItems: 'center', minHeight: '1.5rem', px: 1, borderRadius: 1 }}>
                {row.previousValue}
            </Box>
        )),
        value: cell(row, <CurrentValueCell value={row.value} notes={row.notes}/>),
        unit: cell(row, row.unit),
        count: cell(row, row.count),
        status: cell(row, row.isBranched && <SelectChipIndicator indicatorStatus={row.status} readOnly/>),
    }))

    return (
        <Grid container spacing={1} wrap='nowrap' sx={{ paddingY: 1, borderBottom: '1px grey solid', display: indicatorRows.length ? 'flex' : 'none' }}>
            <Grid sx={{ width: CATEGORY_CARD_WIDTH, flexShrink: 0 }}>
                <IndicatorCategoryCard
                    variant='secondary'
                    height='100%'
                    card={{
                        title: i18n[category],
                        advencement: getCountPercent(categoryCount),
                        status: 0,
                        nbIndicators: categoryCount.filled,
                        nbIndicatorsTotal: categoryCount.required,
                        color,
                    }}
                />
            </Grid>
            <Grid size='grow' sx={{ minWidth: 0 }}>
                <NewTable
                    headers={getHeaders(rpqs.year)}
                    rows={tableRows}
                    onClickRow={row => handleClickRow(row.indicatorRow)}
                    rowsPerPageOptions={[{ value: 1000, label: '1000' }]}
                    WrapperComponent={IndicatorRowsTableBox}
                    disableLocalStorage
                />
            </Grid>

            {dialog}

            {branchedMainCode === P103_2B.code ? (
                <RpqsIndicatorDialogP103_2B
                    open={branchedDialogOpen}
                    setOpen={setBranchedDialogOpen}
                    indicators={backIndicators}
                    rpqs={rpqs}
                    egId={egId}
                    onSaved={onSaved}
                />
            ) : (
                <RpqsIndicatorDialog
                    open={branchedDialogOpen}
                    setOpen={setBranchedDialogOpen}
                    mainCode={branchedMainCode}
                    indicators={backIndicators}
                    rpqs={rpqs}
                    egId={egId}
                    refValues={refValues}
                    onSaved={onSaved}
                />
            )}
        </Grid>
    )
}

CategoryPanel.propTypes = {
    backIndicators: PropTypes.arrayOf(rpqsIndicatorShape),
    category: PropTypes.string,
    searchValue: PropTypes.string,
    rpqs: PropTypes.shape({ year: PropTypes.number, collectivityId: PropTypes.number }),
    egId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refValues: PropTypes.objectOf(PropTypes.number),
    onSaved: PropTypes.func,
}

export const IndicatorPanel = ({
    rpqs = {},
    egId,
    indicators = [],
    refValues = {},
    onSaved = () => {},
}) => {
    const [searchValue, setSearchValue] = useState('')

    const handleSearch = (e) => {
        setSearchValue(e.target.value)
    }

    const year = rpqs.year
    const categories = [SUBSCRIBER, NETWORK, QUALITY, FINANCIAL]

    return (
        <Card>
            <CardTitle
                title={i18n.rpqsEdition}
                actions={[
                    // Search input
                    <Grid
                        container
                        key={'search'}
                        sx={{ position: 'relative', height: '36px' }}
                        py={'4px'}
                        direction={'column'}
                        justifyContent={'center'}
                    >
                        <input
                            placeholder={i18n.search}
                            className='no-margin'
                            onChange={handleSearch}
                        />
                        <Search
                            sx={{ position: 'absolute', right: 4 }}
                        />
                    </Grid>,
                ]}
            />
            <CardContent>
                {year && (
                    <>
                        <Grid container spacing={1} wrap='nowrap'>
                            <Grid sx={{ width: CATEGORY_CARD_WIDTH, flexShrink: 0 }} />
                            <Grid size='grow' sx={{ minWidth: 0 }}>
                                <NewTable
                                    headers={getHeaders(year)}
                                    rows={[]}
                                    noDataDisplay={<></>}
                                    WrapperComponent={AlignedTableBox}
                                    disableLocalStorage
                                />
                            </Grid>
                        </Grid>
                        {categories.map(category => (
                            <CategoryPanel
                                key={category}
                                backIndicators={indicators}
                                category={category}
                                searchValue={searchValue}
                                rpqs={rpqs}
                                egId={egId}
                                refValues={refValues}
                                onSaved={onSaved}
                            />
                        ))}
                    </>
                )}
            </CardContent>
        </Card>
    )
}

IndicatorPanel.propTypes = {
    rpqs: PropTypes.shape({ year: PropTypes.number, collectivityId: PropTypes.number }),
    egId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    indicators: PropTypes.arrayOf(rpqsIndicatorShape),
    refValues: PropTypes.objectOf(PropTypes.number),
    onSaved: PropTypes.func,
}